import React, { useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";

export const SPLIT_KEYS = [
  ["tithe", "Tithe", "Recorded only"], ["rent", "Rent", "→ Rent"], ["case_management", "PACT Fee", "→ PACT Fee"],
  ["savings", "Savings", "→ Savings"], ["checking", "Cash in Hand", "Kept by resident"],
];
const today = () => new Date().toISOString().slice(0, 10);
const RENT = 175, PACT = 125, FLOOR = 428.57;  // 35% / 35% / 30% — the flat amounts apply from $428.57 gross up
const r2 = (v) => Math.round(v * 100) / 100;
/** Standard Outside income split: $175 Rent + $125 PACT Fee, or 35% each when gross is under the safety floor. */
export const standardSplit = (gross) => (gross >= FLOOR ? { rent: RENT, case_management: PACT } : { rent: r2(gross * 0.35), case_management: r2(gross * 0.35) });
const blank = () => ({ pay_period_start: today(), pay_period_end: today(), employer: "", source: "employer_paycheck", gross_amount: "", notes: "",
  split: { tithe: "", rent: "", case_management: "", savings: "", checking: "" } });

const RENT_KEYS = { rent: 1, case_management: 1 };

export default function PaycheckForm({ residentId, onDone, onCancel }) {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({});
  const prefill = (x) => {
    const g = Number(x.gross_amount) || 0;
    const auto = Object.keys(RENT_KEYS).filter((k) => !touched[k]);
    if (x.source !== "outside_income" || g <= 0) return { ...x, split: { ...x.split, ...Object.fromEntries(auto.map((k) => [k, ""])) } };
    const std = standardSplit(g);
    return { ...x, split: { ...x.split, ...Object.fromEntries(Object.entries(std).filter(([k]) => !touched[k]).map(([k, v]) => [k, String(v)])) } };
  };
  const set = (k) => (e) => { const v = e.target.value; setF((x) => (k === "gross_amount" || k === "source" ? prefill({ ...x, [k]: v }) : { ...x, [k]: v })); };
  const setSplit = (k) => (e) => { const v = e.target.value; if (k in RENT_KEYS) setTouched((t) => ({ ...t, [k]: true })); setF((x) => ({ ...x, split: { ...x.split, [k]: v } })); };
  const gross = Number(f.gross_amount) || 0;
  const outside = f.source === "outside_income";
  const total = Object.values(f.split).reduce((a, v) => a + (Number(v) || 0), 0);
  const remaining = Math.round(((Number(f.gross_amount) || 0) - total) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const pc = await finApi.post("/paychecks", { ...f, resident_id: residentId, gross_amount: Number(f.gross_amount),
        split: Object.fromEntries(Object.entries(f.split).map(([k, v]) => [k, Number(v) || 0])) });
      toast.success("Paycheck submitted for verification");
      if (pc.notice) toast.info(pc.notice, { id: "pc-invoice-pending" });
      onDone();
    } catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="fin-paycheck-form" className="p-4 border border-nex-gold/30 rounded-sm space-y-3 bg-white/[0.02]">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="space-y-1"><span className="nds-label-dim text-[9px]">Period start</span>
          <input type="date" required value={f.pay_period_start} onChange={set("pay_period_start")} data-testid="fin-paycheck-form-start" className={`${field} w-full`} /></label>
        <label className="space-y-1"><span className="nds-label-dim text-[9px]">Period end</span>
          <input type="date" required value={f.pay_period_end} onChange={set("pay_period_end")} data-testid="fin-paycheck-form-end" className={`${field} w-full`} /></label>
        <label className="space-y-1"><span className="nds-label-dim text-[9px]">Source</span>
          <select value={f.source} onChange={set("source")} data-testid="fin-paycheck-form-source" className={`${field} w-full`}>
            <option value="employer_paycheck">Employer paycheck</option><option value="outside_income">Outside income</option>
          </select></label>
        <label className="space-y-1"><span className="nds-label-dim text-[9px]">Gross amount</span>
          <input type="number" min="0.01" step="0.01" required value={f.gross_amount} onChange={set("gross_amount")} data-testid="fin-paycheck-form-gross" className={`${field} w-full`} /></label>
      </div>
      <input value={f.employer} onChange={set("employer")} placeholder="Employer" data-testid="fin-paycheck-form-employer" className={`${field} w-full`} />
      {outside && (
        <div data-testid="fin-paycheck-form-split-rule" className="font-mono text-[10px] text-nex-gold/90">
          Standard split: 35% Rent · 35% PACT Fee · 30% resident — $175 Rent + $125 PACT Fee from ${FLOOR} gross; below that, 35% each. Edit if needed.
          {gross > 0 && gross < FLOOR && <span data-testid="fin-paycheck-form-floor-note" className="block text-nex-muted">35% rule in effect: Rent and PACT Fee are each 35% of gross — nothing is short and nothing goes to the Line of Credit.</span>}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {SPLIT_KEYS.map(([k, label, hint]) => (
          <label key={k} className="space-y-1"><span className="nds-label-dim text-[9px]">{label}</span>
            <input type="number" min="0" step="0.01" value={f.split[k]} onChange={setSplit(k)} data-testid={`fin-paycheck-form-split-${k}`} className={`${field} w-full`} />
            <span className="block font-mono text-[9px] text-nex-muted">{hint}</span></label>
        ))}
      </div>
      <input value={f.notes} onChange={set("notes")} placeholder="Notes (optional)" data-testid="fin-paycheck-form-notes" className={`${field} w-full`} />
      <div className="flex items-center gap-2">
        <span data-testid="fin-paycheck-form-remaining" className={`font-mono text-[11px] ${remaining === 0 && f.gross_amount ? "text-emerald-300" : "text-yellow-300"}`}>
          {!f.gross_amount ? "Enter the gross amount" : remaining === 0 ? "Split balanced" : `${money(remaining)} left to split`}
        </span>
        <button type="button" onClick={onCancel} data-testid="fin-paycheck-form-cancel" className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted hover:text-nex-silver">Cancel</button>
        <button type="submit" disabled={busy || remaining !== 0 || !f.gross_amount} data-testid="fin-paycheck-form-submit" className={btnGold}>Submit paycheck</button>
      </div>
    </form>
  );
}
